import { elizaLogger } from '@elizaos/core';
import { ZapperQueryCache, QueryOptimizer } from './queryCache';
import { ZapperErrorHandler, ErrorHandlerConfig } from './errorHandler';
import { RateLimitMiddleware, RateLimitConfig, RateLimitAlgorithm } from './rateLimiter';
import { fetchWithPolyfill } from './fetchWithPolyfill';

/**
 * Configuration for Zapper GraphQL API
 */
export interface ZapperGraphQLConfig {
  apiKey: string;
  apiUrl?: string;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
  debug?: boolean;
  enableCaching?: boolean;
  cacheConfig?: {
    maxSize?: number;
    defaultTTL?: number;
    cleanupInterval?: number;
  };
  errorHandlerConfig?: Partial<ErrorHandlerConfig>;
  rateLimitConfig?: Partial<RateLimitConfig>;
  quotaConfig?: {
    dailyQuota?: number;
    monthlyQuota?: number;
    enableMetrics?: boolean;
  };
}

/**
 * GraphQL query variables type
 */
export interface GraphQLVariables {
  [key: string]: any;
}

/**
 * GraphQL response type
 */
export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{
      line: number;
      column: number;
    }>;
    path?: Array<string | number>;
    extensions?: any;
  }>;
  extensions?: any;
}

/**
 * Query builder for constructing GraphQL queries
 */
export class ZapperQueryBuilder {
  private query: string = '';
  private variables: GraphQLVariables = {};
  private fragments: Map<string, string> = new Map();

  /**
   * Set the main query operation
   */
  setQuery(query: string): this {
    this.query = query;
    return this;
  }

  /**
   * Add variables to the query
   */
  addVariables(variables: GraphQLVariables): this {
    this.variables = { ...this.variables, ...variables };
    return this;
  }

  /**
   * Add a fragment to the query
   */
  addFragment(name: string, fragment: string): this {
    this.fragments.set(name, fragment);
    return this;
  }

  /**
   * Build the complete GraphQL query with fragments
   */
  build(): { query: string; variables: GraphQLVariables } {
    let builtQuery = this.query;

    // Add fragments to the query
    if (this.fragments.size > 0) {
      const fragmentsString = Array.from(this.fragments.values()).join('\n\n');
      builtQuery = `${builtQuery}\n\n${fragmentsString}`;
    }

    return {
      query: builtQuery,
      variables: this.variables,
    };
  }

  /**
   * Clear the builder state
   */
  clear(): this {
    this.query = '';
    this.variables = {};
    this.fragments.clear();
    return this;
  }
}

/**
 * Enhanced GraphQL client for Zapper API
 */
export class ZapperGraphQLClient {
  private config: Required<ZapperGraphQLConfig>;
  private requestCount: number = 0;
  private lastRequestTime: number = 0;
  private cache: ZapperQueryCache | null = null;
  private errorHandler: ZapperErrorHandler;
  private rateLimitMiddleware: RateLimitMiddleware | null = null;

  constructor(config: ZapperGraphQLConfig) {
    this.config = {
      apiUrl: 'https://public.zapper.xyz/graphql',
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      debug: false,
      enableCaching: true,
      cacheConfig: {
        maxSize: 1000,
        defaultTTL: 300000,
        cleanupInterval: 60000,
      },
      errorHandlerConfig: {},
      rateLimitConfig: {
        algorithm: RateLimitAlgorithm.TOKEN_BUCKET,
        maxRequests: 100,
        windowMs: 60000,
        enableQueue: true,
        enableMetrics: true,
      },
      quotaConfig: {
        dailyQuota: 10000,
        monthlyQuota: 100000,
        enableMetrics: true,
      },
      ...config,
    };

    // Initialize cache if enabled
    if (this.config.enableCaching) {
      this.cache = new ZapperQueryCache(this.config.cacheConfig);
    }

    // Initialize error handler
    this.errorHandler = new ZapperErrorHandler(this.config.errorHandlerConfig);

    // Initialize rate limiting if configured
    if (this.config.rateLimitConfig && this.config.quotaConfig) {
      this.rateLimitMiddleware = new RateLimitMiddleware(
        this.config.rateLimitConfig as RateLimitConfig,
        {
          dailyQuota: this.config.quotaConfig.dailyQuota || 10000,
          monthlyQuota: this.config.quotaConfig.monthlyQuota || 100000,
          enableMetrics: this.config.quotaConfig.enableMetrics || true,
        }
      );
    }
  }

  /**
   * Execute a GraphQL query with retry logic and caching
   */
  async query<T = any>(
    query: string,
    variables?: GraphQLVariables,
    options?: {
      timeout?: number;
      retryAttempts?: number;
      retryDelay?: number;
      useCache?: boolean;
      cacheTTL?: number;
    }
  ): Promise<GraphQLResponse<T>> {
    const mergedOptions = {
      timeout: this.config.timeout,
      retryAttempts: this.config.retryAttempts,
      retryDelay: this.config.retryDelay,
      useCache: this.config.enableCaching,
      cacheTTL: this.config.cacheConfig?.defaultTTL,
      ...options,
    };

    // Check cache first
    if (mergedOptions.useCache && this.cache) {
      const cachedResult = this.cache.get<T>(query, variables);
      if (cachedResult) {
        if (this.config.debug) {
          elizaLogger.debug('GraphQL query served from cache', {
            query: query.substring(0, 100) + '...',
            variables,
          });
        }
        return cachedResult;
      }
    }

    // Check rate limits
    if (this.rateLimitMiddleware) {
      const limitCheck = await this.rateLimitMiddleware.checkRequest({
        query: query.substring(0, 100) + '...',
        variables,
      });

      if (!limitCheck.allowed) {
        const error = new Error('Rate limit exceeded');
        (error as any).retryAfter = limitCheck.rateLimit.retryAfter;
        (error as any).remaining = limitCheck.rateLimit.remaining;
        (error as any).resetTime = limitCheck.rateLimit.resetTime;
        (error as any).quotaRemaining = limitCheck.quota.remaining;
        throw error;
      }

      if (this.config.debug) {
        elizaLogger.debug('Rate limit check passed', {
          remaining: limitCheck.rateLimit.remaining,
          quotaRemaining: limitCheck.quota.remaining,
        });
      }
    }

    // Optimize query before execution
    const optimizedQuery = QueryOptimizer.removeUnusedFields(query, []);
    const optimizedVariables = QueryOptimizer.optimizeVariables(variables || {});

    // Use error handler for execution with retry logic
    return this.errorHandler.executeWithRetry(
      async () => {
        if (this.config.debug) {
          elizaLogger.debug('Executing GraphQL query', {
            query: optimizedQuery.substring(0, 100) + '...',
            variables: optimizedVariables,
            fromCache: false,
          });
        }

        const response = await this.executeQuery<T>(
          optimizedQuery,
          optimizedVariables,
          mergedOptions.timeout
        );

        if (response.errors && response.errors.length > 0) {
          elizaLogger.warn('GraphQL query returned errors', { errors: response.errors });
          // Treat GraphQL errors as failures for retry logic
          throw response;
        }

        // Cache successful response
        if (mergedOptions.useCache && this.cache && response.data) {
          this.cache.set(query, variables, response, mergedOptions.cacheTTL);
        }

        return response;
      },
      {
        maxAttempts: mergedOptions.retryAttempts,
        baseDelay: mergedOptions.retryDelay,
        backoffMultiplier: 2,
        jitter: true,
      },
      {
        query: optimizedQuery.substring(0, 100) + '...',
        variables: optimizedVariables,
        useCache: mergedOptions.useCache,
      }
    );
  }

  /**
   * Execute a GraphQL mutation
   */
  async mutate<T = any>(
    mutation: string,
    variables?: GraphQLVariables,
    options?: {
      timeout?: number;
      retryAttempts?: number;
      retryDelay?: number;
    }
  ): Promise<GraphQLResponse<T>> {
    return this.query<T>(mutation, variables, options);
  }

  /**
   * Execute multiple queries in parallel
   */
  async batchQuery<T = any>(
    queries: Array<{
      query: string;
      variables?: GraphQLVariables;
      key?: string;
    }>,
    options?: {
      timeout?: number;
      retryAttempts?: number;
      retryDelay?: number;
    }
  ): Promise<Array<GraphQLResponse<T>>> {
    const promises = queries.map(({ query, variables }) =>
      this.query<T>(query, variables, options)
    );

    return Promise.all(promises);
  }

  /**
   * Get client statistics
   */
  getStats(): {
    requestCount: number;
    lastRequestTime: number;
    avgRequestTime: number;
    cacheStats?: any;
    errorStats?: any;
  } {
    const stats = {
      requestCount: this.requestCount,
      lastRequestTime: this.lastRequestTime,
      avgRequestTime: this.lastRequestTime / Math.max(this.requestCount, 1),
    };

    const result: any = { ...stats };

    if (this.cache) {
      result.cacheStats = this.cache.getStats();
    }

    if (this.errorHandler) {
      result.errorStats = this.errorHandler.getMetrics();
    }

    if (this.rateLimitMiddleware) {
      result.rateLimitStats = this.rateLimitMiddleware.getStatus();
    }

    return result;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    if (this.cache) {
      this.cache.clear();
    }
  }

  /**
   * Invalidate cache entry
   */
  invalidateCache(query: string, variables?: GraphQLVariables): boolean {
    if (this.cache) {
      return this.cache.invalidate(query, variables);
    }
    return false;
  }

  /**
   * Invalidate cache entries by pattern
   */
  invalidateCachePattern(pattern: string): number {
    if (this.cache) {
      return this.cache.invalidatePattern(pattern);
    }
    return 0;
  }

  /**
   * Optimize cache
   */
  optimizeCache(): { entriesRemoved: number; memoryFreed: number } {
    if (this.cache) {
      return this.cache.optimize();
    }
    return { entriesRemoved: 0, memoryFreed: 0 };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ZapperGraphQLConfig>): void {
    this.config = { ...this.config, ...config };

    // Reinitialize cache if caching config changed
    if (config.enableCaching !== undefined || config.cacheConfig) {
      if (this.cache) {
        this.cache.destroy();
      }

      if (this.config.enableCaching) {
        this.cache = new ZapperQueryCache(this.config.cacheConfig);
      } else {
        this.cache = null;
      }
    }
  }

  /**
   * Get error handler metrics
   */
  getErrorMetrics(): any {
    return this.errorHandler.getMetrics();
  }

  /**
   * Reset error metrics
   */
  resetErrorMetrics(): void {
    this.errorHandler.resetMetrics();
  }

  /**
   * Get circuit breaker status
   */
  getCircuitBreakerStatus(): any {
    return this.errorHandler.getCircuitBreakerStatus();
  }

  /**
   * Reset circuit breaker
   */
  resetCircuitBreaker(): void {
    this.errorHandler.resetCircuitBreaker();
  }

  /**
   * Get rate limit status
   */
  getRateLimitStatus(): any {
    return this.rateLimitMiddleware?.getStatus() || null;
  }

  /**
   * Reset rate limits
   */
  resetRateLimits(): void {
    if (this.rateLimitMiddleware) {
      this.rateLimitMiddleware.reset();
    }
  }

  /**
   * Destroy client and cleanup resources
   */
  destroy(): void {
    if (this.cache) {
      this.cache.destroy();
      this.cache = null;
    }
  }

  /**
   * Execute the actual GraphQL query
   */
  private async executeQuery<T>(
    query: string,
    variables?: GraphQLVariables,
    timeout?: number
  ): Promise<GraphQLResponse<T>> {
    const startTime = Date.now();
    this.requestCount++;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout || this.config.timeout);

    try {
      const response = await fetchWithPolyfill(this.config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // CRITICAL: Use 'x-zapper-api-key' header, NOT 'Authorization: Bearer'
          // Bearer token format causes 401 authentication errors
          'x-zapper-api-key': this.config.apiKey,
          'User-Agent': 'ElizaOS-ZapperPlugin/1.0.0',
        },
        body: JSON.stringify({
          query,
          variables: variables || {},
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }

      const result = (await response.json()) as GraphQLResponse<T>;

      this.lastRequestTime = Date.now() - startTime;

      if (this.config.debug) {
        elizaLogger.debug('GraphQL query completed', {
          status: response.status,
          duration: this.lastRequestTime,
          hasErrors: !!(result.errors && result.errors.length > 0),
        });
      }

      return result;
    } catch (error) {
      clearTimeout(timeoutId);

      if ((error as any).name === 'AbortError') {
        throw new Error(`Query timeout after ${timeout || this.config.timeout}ms`);
      }

      throw error;
    }
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Common GraphQL fragments for Zapper API
 */
export const ZapperFragments = {
  TOKEN_BALANCE: `
    fragment TokenBalance on TokenBalance {
      balance
      balanceUSD
      symbol
      name
      network
      address
      decimals
      price
      priceUSD
    }
  `,

  PORTFOLIO_BALANCE: `
    fragment PortfolioBalance on PortfolioBalance {
      network
      address
      balance
      balanceUSD
      symbol
      name
      decimals
      price
      priceUSD
    }
  `,

  TRANSACTION_DETAIL: `
    fragment TransactionDetail on Transaction {
      hash
      blockNumber
      timestamp
      from
      to
      value
      gasPrice
      gasUsed
      gasLimit
      status
      network
      type
      methodName
      contractAddress
    }
  `,

  NFT_DETAIL: `
    fragment NFTDetail on NFT {
      tokenId
      contractAddress
      network
      name
      description
      image
      imagePreview
      imageThumbnail
      collectionName
      collectionSlug
      floorPrice
      floorPriceUSD
      lastSalePrice
      lastSalePriceUSD
      rarity
      traits
    }
  `,

  APP_POSITION: `
    fragment AppPosition on AppPosition {
      key
      address
      network
      appId
      appName
      appImage
      groupId
      groupName
      positionType
      balance
      balanceUSD
      suppliedTokens
      borrowedTokens
      rewardTokens
      apy
      healthFactor
    }
  `,

  NETWORK_INFO: `
    fragment NetworkInfo on Network {
      network
      name
      chainId
      isTestnet
      explorerUrl
      rpcUrl
      nativeTokenSymbol
      nativeTokenName
      nativeTokenDecimals
      logoUrl
    }
  `,
};

/**
 * Common GraphQL queries for Zapper API
 */
export const ZapperQueries = {
  PORTFOLIO_V2: `
    query PortfolioV2($addresses: [Address!]!, $chainIds: [Int!]) {
      portfolioV2(addresses: $addresses, chainIds: $chainIds) {
        tokenBalances {
          byToken {
            edges {
              node {
                ...TokenBalance
              }
            }
          }
        }
      }
    }
  `,

  TRANSACTION_HISTORY_V2: `
    query TransactionHistoryV2($addresses: [Address!]!, $chainIds: [Int!], $limit: Int, $cursor: String) {
      transactionHistoryV2(addresses: $addresses, chainIds: $chainIds, limit: $limit, cursor: $cursor) {
        edges {
          node {
            ...TransactionDetail
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `,

  TRANSACTION_DETAILS_V2: `
    query TransactionDetailsV2($hash: String!, $chainId: Int!) {
      transactionDetailsV2(hash: $hash, chainId: $chainId) {
        ...TransactionDetail
        logs {
          address
          topics
          data
          logIndex
          blockNumber
          transactionHash
          transactionIndex
          blockHash
          removed
        }
        traces {
          type
          from
          to
          value
          gas
          gasUsed
          input
          output
          error
        }
        decodedLogs {
          name
          signature
          inputs
          address
          logIndex
        }
      }
    }
  `,

  APP_POSITIONS_V2: `
    query AppPositionsV2($addresses: [Address!]!, $chainIds: [Int!]) {
      appPositionsV2(addresses: $addresses, chainIds: $chainIds) {
        edges {
          node {
            ...AppPosition
          }
        }
      }
    }
  `,

  NFT_COLLECTION: `
    query NFTCollection($address: Address!, $network: Network!) {
      nftCollection(address: $address, network: $network) {
        name
        description
        image
        totalSupply
        floorPrice
        floorPriceUSD
        volume24h
        volume24hUSD
        owners
        items {
          ...NFTDetail
        }
      }
    }
  `,

  ACCOUNTS: `
    query Accounts($addresses: [Address!]!) {
      accounts(addresses: $addresses) {
        address
        ens
        farcaster {
          fid
          username
          displayName
          pfp
          bio
          followers
          following
          verifications
        }
        basename
        stats {
          totalValueUSD
          totalTransactions
          firstTransactionDate
          lastTransactionDate
          networksUsed
        }
      }
    }
  `,

  // NETWORKS query removed - Zapper API doesn't support 'networks' field
  // Use individual network queries instead: network(chainId: Int!, vm: VirtualMachineType!)
  // Example: query Network($chainId: Int!) { network(chainId: $chainId, vm: EVM) { name chainId } }

  HISTORICAL_PRICE: `
    query HistoricalPrice($tokenAddress: Address!, $chainId: Int!, $timeframe: String!, $limit: Int) {
      historicalPrice(tokenAddress: $tokenAddress, chainId: $chainId, timeframe: $timeframe, limit: $limit) {
        timestamp
        price
        volume
        marketCap
      }
    }
  `,
};

/**
 * Utility functions for GraphQL query construction
 */
export const ZapperGraphQLUtils = {
  /**
   * Create a new query builder instance
   */
  createQueryBuilder(): ZapperQueryBuilder {
    return new ZapperQueryBuilder();
  },

  /**
   * Create a new GraphQL client instance
   */
  createClient(config: ZapperGraphQLConfig): ZapperGraphQLClient {
    return new ZapperGraphQLClient(config);
  },

  /**
   * Validate GraphQL query syntax
   */
  validateQuery(query: string): boolean {
    try {
      // Basic validation - check for required GraphQL structure
      const hasQuery = query.includes('query') || query.includes('mutation');
      const hasOpenBrace = query.includes('{');
      const hasCloseBrace = query.includes('}');

      return hasQuery && hasOpenBrace && hasCloseBrace;
    } catch {
      return false;
    }
  },

  /**
   * Extract variables from GraphQL query
   */
  extractVariables(query: string): string[] {
    const variablePattern = /\$(\w+):/g;
    const variables: string[] = [];
    let match;

    while ((match = variablePattern.exec(query)) !== null) {
      variables.push(match[1]);
    }

    return variables;
  },

  /**
   * Combine multiple queries into a single batch query
   */
  combineQueries(
    queries: Array<{ alias: string; query: string; variables?: GraphQLVariables }>
  ): string {
    const combinedQuery = queries
      .map(({ alias, query }) => {
        // Remove outer query wrapper and add alias
        const innerQuery = query.replace(/^query\s+\w*\s*\([^)]*\)\s*{/, '').replace(/}$/, '');
        return `${alias}: ${innerQuery}`;
      })
      .join('\n');

    return `query CombinedQuery { ${combinedQuery} }`;
  },

  /**
   * Format GraphQL errors for logging
   */
  formatGraphQLErrors(errors: Array<{ message: string; locations?: any; path?: any }>): string {
    return errors
      .map((error, index) => {
        let formatted = `Error ${index + 1}: ${error.message}`;

        if (error.locations) {
          formatted += ` (Line ${error.locations[0]?.line}, Column ${error.locations[0]?.column})`;
        }

        if (error.path) {
          formatted += ` at path: ${error.path.join('.')}`;
        }

        return formatted;
      })
      .join('\n');
  },

  /**
   * Check if response has errors
   */
  hasErrors(response: GraphQLResponse): boolean {
    return !!(response.errors && response.errors.length > 0);
  },

  /**
   * Get data from GraphQL response safely
   */
  getDataSafely<T>(response: GraphQLResponse<T>): T | null {
    return response.data || null;
  },

  /**
   * Create a paginated query
   */
  createPaginatedQuery(baseQuery: string, limit: number = 50, cursor?: string): string {
    const variables = cursor ? `(limit: ${limit}, cursor: "${cursor}")` : `(limit: ${limit})`;

    return baseQuery.replace(/\([^)]*\)/, variables);
  },

  /**
   * Extract pagination info from response
   */
  extractPaginationInfo(response: GraphQLResponse): {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor?: string;
    endCursor?: string;
  } | null {
    // Try to find pageInfo in the response
    const findPageInfo = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return null;

      if (obj.pageInfo) return obj.pageInfo;

      for (const key in obj) {
        const result = findPageInfo(obj[key]);
        if (result) return result;
      }

      return null;
    };

    const pageInfo = findPageInfo(response.data);

    return pageInfo
      ? {
          hasNextPage: pageInfo.hasNextPage || false,
          hasPreviousPage: pageInfo.hasPreviousPage || false,
          startCursor: pageInfo.startCursor,
          endCursor: pageInfo.endCursor,
        }
      : null;
  },
};

/**
 * Default export for easy importing
 */
export default {
  ZapperGraphQLClient,
  ZapperQueryBuilder,
  ZapperFragments,
  ZapperQueries,
  ZapperGraphQLUtils,
};
